import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Upload, CheckCircle, Zap, Download, Trash2, Edit3, Highlighter, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ReferenceDocumentsPicker, ReferenceDocumentsNote, DiscrepancySection, MAX_SUPPORTING_DOCS } from '../components/ContractDiscrepancies';

const EMPTY_DISCREPANCY = { files: [], status: 'idle', message: null, result: null, error: null };

const ContractRedline = () => {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [annotatedPdfUrl, setAnnotatedPdfUrl] = useState(null);
  const [annotations, setAnnotations] = useState([]);
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [textBlocks, setTextBlocks] = useState([]); // Textract blocks for accurate positioning
  const [metadata, setMetadata] = useState(null); // Analysis metadata (usedOCR, pdfType, etc.)
  const [isWordDocument, setIsWordDocument] = useState(false); // Track if current file is Word
  const [redlinedDocUrl, setRedlinedDocUrl] = useState(null); // Blob URL for redlined Word document download
  /** `user_chat_sessions.id` for Chat History (Contract Redline row + download notes) */
  const [contractRedlineSessionId, setContractRedlineSessionId] = useState(null);
  const [progress, setProgress] = useState({ elapsed: 0, estimated: 0, remaining: 0, percentage: 0 }); // Progress tracking
  // What the server is doing right now on a long review. A silent spinner for
  // ten minutes reads as broken even when it is working.
  const [progressLabel, setProgressLabel] = useState('');
  // Set by the page-count probe; a scan routes to the job path at any size.
  const [fileIsScanned, setFileIsScanned] = useState(false);
  const progressIntervalRef = useRef(null); // Timer interval reference
  const fileInputRef = useRef(null);
  // Exhibits, addenda, supplements read with the contract; and the scope/proposal
  // comparison run after it. Both belong to the contract loaded now.
  const [referenceFiles, setReferenceFiles] = useState([]);
  const [discrepancy, setDiscrepancy] = useState(EMPTY_DISCREPANCY);

  useEffect(() => {
    setReferenceFiles([]);
    setDiscrepancy(EMPTY_DISCREPANCY);
  }, [file]);

  // Cleanup progress timer on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);


  const isValidFile = (file) => {
    const validTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/msword', // .doc
    ];
    return validTypes.includes(file.type);
  };

  const getFileTypeName = (file) => {
    if (file.type === 'application/pdf') return 'PDF';
    if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return 'Word document';
    if (file.type === 'application/msword') return 'Word document';
    return 'document';
  };

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && isValidFile(selectedFile)) {
      setFile(selectedFile);
      setAnalysis(null);
      setAnnotations([]);
      setTextBlocks([]);
      setMetadata(null);
      setRedlinedDocUrl(null);
      
      // Detect file type
      const isWord = selectedFile.type.includes('word') || selectedFile.name.toLowerCase().endsWith('.docx');
      setIsWordDocument(isWord);
      
      // Create preview URL (for PDFs only)
      if (selectedFile.type === 'application/pdf') {
        const url = URL.createObjectURL(selectedFile);
        setPdfUrl(url);
      } else {
        setPdfUrl(null);
      }
      
      const fileType = getFileTypeName(selectedFile);
      toast.success(`${fileType} loaded successfully!`);
    } else {
      toast.error('Please select a valid PDF or Word document (.docx, .doc)');
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && isValidFile(droppedFile)) {
      setFile(droppedFile);
      setAnalysis(null);
      setAnnotations([]);
      setTextBlocks([]);
      setMetadata(null);
      setRedlinedDocUrl(null);
      
      // Detect file type
      const isWord = droppedFile.type.includes('word') || droppedFile.name.toLowerCase().endsWith('.docx');
      setIsWordDocument(isWord);
      
      // Create preview URL (for PDFs only)
      if (droppedFile.type === 'application/pdf') {
        const url = URL.createObjectURL(droppedFile);
        setPdfUrl(url);
      } else {
        setPdfUrl(null);
      }
      
      const fileType = getFileTypeName(droppedFile);
      toast.success(`${fileType} loaded successfully!`);
    } else {
      toast.error('Please drop a valid PDF or Word document (.docx, .doc)');
    }
  };

  const handleSampleDocument = async (samplePath, fileName) => {
    try {
      const response = await fetch(samplePath);
      const blob = await response.blob();
      
      // Create a File object from the blob
      const file = new File([blob], fileName, { 
        type: blob.type || (fileName.endsWith('.pdf') ? 'application/pdf' : 'application/msword')
      });
      
      // Use the same logic as handleFileSelect
      if (isValidFile(file)) {
        setFile(file);
        setAnalysis(null);
        setAnnotations([]);
        setTextBlocks([]);
        setMetadata(null);
        setRedlinedDocUrl(null);
        
        // Detect file type
        const isWord = file.type.includes('word') || fileName.toLowerCase().endsWith('.docx') || fileName.toLowerCase().endsWith('.doc');
        setIsWordDocument(isWord);
        
        // Create preview URL (for PDFs only)
        if (file.type === 'application/pdf') {
          const url = URL.createObjectURL(file);
          setPdfUrl(url);
        } else {
          setPdfUrl(null);
        }
        
        const fileType = getFileTypeName(file);
        toast.success(`Sample ${fileType} loaded successfully!`);
      } else {
        toast.error('Invalid sample document format');
      }
    } catch (error) {
      console.error('Error loading sample document:', error);
      toast.error('Failed to load sample document');
    }
  };

  // Calculate estimated time based on page count (10 pages = 2 minutes = 120 seconds)
  const calculateEstimatedTime = (pageCount) => {
    if (!pageCount || pageCount <= 0) return 480; // Default 8 minutes for larger files
    // 1 page = 12 seconds (120 seconds / 10 pages)
    return Math.max(60, pageCount * 12); // Minimum 1 minute
  };

  // Get actual page count from server (for both PDF and Word documents)
  const getPageCountFromServer = async (file) => {
    try {
      const formData = new FormData();
      formData.append('contract', file);

      const response = await axios.post('/api/contracts/page-count', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        // The same probe reports whether there is a text layer, which decides
        // the route: a scan needs the OCR-capable path at any page count.
        setFileIsScanned(!!response.data.isScanned);
        return { totalPages: response.data.totalPages, isScanned: !!response.data.isScanned };
      } else {
        throw new Error(response.data.error || 'Failed to get page count');
      }
    } catch (error) {
      console.warn('Failed to get page count from server, using estimate:', error);
      // Fallback to estimate
      return Math.max(1, Math.ceil(file.size / (1024 * 1024) * (isWordDocument ? 8 : 10)));
    }
  };

  // Start progress timer (covers both analysis and preview generation)
  const startProgressTimer = (estimatedSeconds) => {
    const startTime = Date.now();
    // Use calculated time directly, but cap at 8 minutes maximum for larger files
    const maxTime = Math.min(estimatedSeconds, 480); // Maximum 8 minutes
    
    // Clear any existing interval
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    
    progressIntervalRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      const remaining = Math.max(0, maxTime - elapsed);
      const percentage = Math.min(95, (elapsed / maxTime) * 100); // Cap at 95% until completion
      
      setProgress({
        elapsed,
        estimated: maxTime,
        remaining,
        percentage
      });
    }, 1000); // Update every second
  };

  // Stop progress timer
  const stopProgressTimer = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    setProgress({ elapsed: 0, estimated: 0, remaining: 0, percentage: 100 });
  };

  /* Upload straight to S3, run the review as a job, and poll it. Returns the
     same payload shape the multipart route does, so everything downstream —
     annotations, preview, redline — is unchanged. */
  const runLargeReview = async (file, pageEstimate) => {
    setProgressLabel(`Uploading ${(file.size / 1048576).toFixed(0)} MB…`);
    const presignRes = await axios.post('/api/large-file/presign', {
      filename: file.name, contentType: file.type || 'application/pdf', size: file.size,
    });
    const { uploadUrl, s3Key } = presignRes.data || {};
    if (!uploadUrl || !s3Key) throw new Error('Could not start the upload (presign failed).');

    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.timeout = 30 * 60 * 1000;
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type || 'application/pdf');
      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable) {
          setProgressLabel(`Uploading ${Math.round((ev.loaded / ev.total) * 100)}%…`);
        }
      };
      xhr.onload = () => (xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(xhr.status === 403
            ? "S3 rejected the upload: the server's AWS identity cannot write to the uploads bucket."
            : `Upload failed (HTTP ${xhr.status})`)));
      xhr.onerror = () => reject(new Error('Upload failed — check your connection and retry.'));
      xhr.ontimeout = () => reject(new Error('Upload timed out.'));
      xhr.send(file);
    });

    const start = await axios.post('/api/contracts/analyze-large', { s3Key, fileName: file.name });
    const jobId = start.data?.jobId;
    if (!jobId) throw new Error(start.data?.error || 'Could not start the review.');

    setProgressLabel(`Reading all ${pageEstimate} pages…`);
    for (;;) {
      await new Promise((r) => setTimeout(r, 3000));
      const { data } = await axios.get(`/api/contracts/analyze-large/${jobId}`);
      if (data.stage?.label) {
        setProgressLabel(data.stage.total
          ? `${data.stage.label}${data.stage.lanes ? ` · ${data.stage.lanes} in parallel` : ''}`
          : data.stage.label);
      }
      if (data.status === 'error') throw new Error(data.error || 'The review failed.');
      if (data.status === 'done') return data.result;
    }
  };

  const analyzeContract = async () => {
    if (!file) {
      toast.error('Please upload a PDF first');
      return;
    }

    setAnalyzing(true);
    
    // Get actual page count from server (for both PDF and Word documents)
    // This avoids CSP violations from loading PDF.js from CDN
    let actualPages;
    let scanned = false;
    try {
      const probe = await getPageCountFromServer(file);
      actualPages = probe.totalPages;
      scanned = probe.isScanned;
    } catch (error) {
      console.warn('Failed to get page count from server, using estimate:', error);
      // Fallback to estimate
      actualPages = Math.max(1, Math.ceil(file.size / (1024 * 1024) * (isWordDocument ? 8 : 10)));
    }
    
    const estimatedTime = calculateEstimatedTime(actualPages);
    
    // Start progress timer with actual page count
    startProgressTimer(estimatedTime);
    
    try {
      /* A long or scanned contract cannot go through the multipart route: it
         caps at 50 MB, OCRs the whole document in one Textract job with a
         five-minute ceiling, and holds a single request open for the entire
         review. Those contracts take the same road the drawing tools use —
         straight to S3, reviewed as a background job, polled from here. */
      const LARGE_BYTES = 15 * 1024 * 1024;
      const LARGE_PAGES = 120;
      // A scan takes this road at any size: the multipart route OCRs the whole
      // document in one Textract job with a five-minute ceiling, while the job
      // route OCRs 40 pages at a time in parallel and reports progress.
      let response;
      if (file.size > LARGE_BYTES || actualPages > LARGE_PAGES || scanned) {
        if (referenceFiles.length) {
          toast('Supporting documents are not read for contracts this large or scanned — the contract alone is reviewed.');
        }
        response = { data: await runLargeReview(file, actualPages) };
      } else {
        const formData = new FormData();
        formData.append('contract', file);
        referenceFiles.forEach((f) => formData.append('supporting', f));
        response = await axios.post('/api/contracts/analyze', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      }

      const unreadable = (response.data.metadata?.referenceDocuments || []).filter((d) => d.error);
      if (unreadable.length) toast.error(`${unreadable.length} supporting document(s) could not be read and were skipped`);
      const chatSid = response.data.chatSessionId;
      setAnalysis(response.data.analysis);
      if (chatSid != null && chatSid !== '') {
        setContractRedlineSessionId(Number(chatSid));
      }
      const newAnnotations = response.data.annotations || [];
      setAnnotations(newAnnotations);
      
      // Save text blocks and metadata for accurate positioning
      const newTextBlocks = response.data.textBlocks || [];
      const newMetadata = response.data.metadata || {};
      setTextBlocks(newTextBlocks);
      setMetadata(newMetadata);
      
      // Timer was already started with actual page count, no need to update
      
      // Log OCR info (don't show toast yet - still processing preview)
      if (newMetadata.usedOCR) {
        console.log(`📸 Image-based PDF detected - ${newTextBlocks.length} text blocks available for positioning`);
      }
      
      // Automatically generate annotated preview (timer continues, analyzing stays true)
      try {
        await generateAnnotatedPreview(newAnnotations, newTextBlocks, newMetadata, chatSid);
        
        // Show success toast after preview generation completes
        if (newMetadata.usedOCR) {
          toast.success(`Analysis complete! (OCR used: ${newMetadata.pdfType})`);
        } else {
          toast.success('Contract analysis complete!');
        }
      } catch (previewError) {
        // Preview errors are handled in generateAnnotatedPreview, but we still want to continue
        console.error('Preview generation failed:', previewError);
        // Still show success for analysis even if preview failed
        if (newMetadata.usedOCR) {
          toast.success(`Analysis complete! (OCR used: ${newMetadata.pdfType})`);
        } else {
          toast.success('Contract analysis complete!');
        }
      }
      
      // Stop timer after both analysis and preview are complete
      stopProgressTimer();
      setAnalyzing(false);
      setProgressLabel('');
    } catch (error) {
      console.error('Analysis error:', error);
      // The large path throws plain Errors with a usable message; the axios
      // path carries the server's. Show whichever we actually got.
      toast.error(error.response?.data?.error || error.message || 'Failed to analyze contract');
      stopProgressTimer();
      setAnalyzing(false);
      setProgressLabel('');
    }
  };

  const downloadRedlinedPDF = async () => {
    if (!file || !analysis) {
      toast.error('Please analyze a contract first');
      return;
    }

    // Use the already-generated annotated document instead of regenerating
    if (isWordDocument && redlinedDocUrl) {
      // For Word documents, download the already-generated blob URL
      const link = document.createElement('a');
      link.href = redlinedDocUrl;
      link.setAttribute('download', `redlined_${file.name}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Redlined Word document downloaded!');
      return;
    } else if (!isWordDocument && annotatedPdfUrl) {
      // For PDFs, download the already-generated annotated PDF
      // Use the blob URL directly without fetching (avoids CSP issues)
      try {
        const link = document.createElement('a');
        link.href = annotatedPdfUrl;
        link.setAttribute('download', `redlined_${file.name}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Redlined PDF downloaded!');
        return;
      } catch (error) {
        console.error('Download error:', error);
        toast.error('Failed to download redlined PDF');
        return;
      }
    }

    // Fallback: If annotated document is not available, regenerate it
    // This should rarely happen, but provides a safety net
    console.warn('⚠️ Annotated document not available, regenerating...');
    try {
      const formData = new FormData();
      formData.append('contract', file);
      formData.append('annotations', JSON.stringify(annotations));
      if (contractRedlineSessionId != null) {
        formData.append('contractRedlineSessionId', String(contractRedlineSessionId));
      }
      
      // Pass text blocks for accurate OCR-based positioning
      if (textBlocks && textBlocks.length > 0) {
        formData.append('textBlocks', JSON.stringify(textBlocks));
        formData.append('usedOCR', metadata?.usedOCR ? 'true' : 'false');
        console.log(`📊 Sending ${textBlocks.length} text blocks for accurate positioning`);
      }

      const response = await axios.post('/api/contracts/redline', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `redlined_${file.name}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Redlined PDF downloaded!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download redlined PDF');
    }
  };

  const generateAnnotatedPreview = async (annotationsToUse, blocksToUse, metadataToUse, sessionIdForHistory = null) => {
    if (!file) return;
    
    setGeneratingPreview(true);
    // Timer continues running - no need to update here, it will progress naturally
    
    try {
      const formData = new FormData();
      formData.append('contract', file);
      formData.append('annotations', JSON.stringify(annotationsToUse));
      const sid = sessionIdForHistory ?? contractRedlineSessionId;
      if (sid != null && sid !== '') {
        formData.append('contractRedlineSessionId', String(sid));
      }
      
      // Pass text blocks for accurate OCR-based positioning (PDF only)
      const blocks = blocksToUse || textBlocks;
      const meta = metadataToUse || metadata;
      if (blocks && blocks.length > 0) {
        formData.append('textBlocks', JSON.stringify(blocks));
        formData.append('usedOCR', meta?.usedOCR ? 'true' : 'false');
        console.log(`📊 Sending ${blocks.length} text blocks for accurate positioning`);
      }

      const response = await axios.post('/api/contracts/preview', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        responseType: 'blob',
      });

      console.log('📄 Received blob:', response.data.size, 'bytes', `(${isWordDocument ? 'Word' : 'PDF'})`);
      
      if (response.data.size === 0) {
        throw new Error('Received empty document from server');
      }
      
      // For Word documents: Cache the redlined .docx and display in ONLYOFFICE
      // For PDFs: Display annotated PDF in iframe
      if (isWordDocument) {
        // Create blob URL for redlined Word document download
        const redlinedDocxBlob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
        });
        const url = URL.createObjectURL(redlinedDocxBlob);
        setRedlinedDocUrl(url);
        console.log('✅ Redlined Word document generated for download');
      } else {
        // For PDF: Display in iframe
        // Create URL for annotated PDF (exactly as in previous working version)
        const blob = new Blob([response.data], { type: 'application/pdf' });
        console.log('📄 Received PDF blob:', blob.size, 'bytes');
        
        if (blob.size === 0) {
          throw new Error('Received empty PDF from server');
        }
        
        const url = URL.createObjectURL(blob);
        setAnnotatedPdfUrl(url);
        
        console.log('✅ Annotated preview generated:', url);
      }
      
    } catch (error) {
      console.error('Preview generation error:', error);
      toast.error('Document structure issue - showing original without annotations', {
        duration: 3000,
      });
      // Still allow viewing the original document
      setShowAnnotations(false);
      // Note: analyzing state is managed in analyzeContract, not here
    } finally {
      setGeneratingPreview(false);
    }
  };

  const clearFile = () => {
    // Revoke blob URLs to prevent memory leaks
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
    }
    if (annotatedPdfUrl) {
      URL.revokeObjectURL(annotatedPdfUrl);
    }
    if (redlinedDocUrl) {
      URL.revokeObjectURL(redlinedDocUrl);
    }
    
    setFile(null);
    setAnalysis(null);
    setAnnotations([]);
    setPdfUrl(null);
    setAnnotatedPdfUrl(null);
    setTextBlocks([]);
    setMetadata(null);
    setRedlinedDocUrl(null);
    setIsWordDocument(false);
    setContractRedlineSessionId(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /** Compare scope documents and proposals against the finished analysis. */
  const runDiscrepancyCheck = async () => {
    if (!file || !analysis || !discrepancy.files.length || discrepancy.status === 'running') return;
    setDiscrepancy((d) => ({ ...d, status: 'running', message: 'Uploading documents', error: null }));
    try {
      const formData = new FormData();
      formData.append('contract', file);
      formData.append('analysis', JSON.stringify({ summary: analysis.summary, findings: analysis.findings || [] }));
      discrepancy.files.forEach((f) => formData.append('documents', f));
      const start = await axios.post('/api/contracts/discrepancies', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const jobId = start.data?.jobId;
      if (!jobId) throw new Error(start.data?.error || 'Could not start the comparison.');
      const deadline = Date.now() + 30 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data } = await axios.get(`/api/contracts/discrepancies/${jobId}`);
        if (data.status === 'error') throw new Error(data.error || 'The comparison failed.');
        if (data.status === 'done') {
          setDiscrepancy((d) => ({ ...d, status: 'done', message: null, result: data.result?.discrepancies || null }));
          toast.success('Scope & proposal comparison is ready');
          return;
        }
        const pct = data.progress?.total ? ` — ${data.progress.done} of ${data.progress.total} ${data.progress.unit || ''}` : '';
        setDiscrepancy((d) => ({ ...d, message: `${data.message || 'Comparing'}${pct}` }));
      }
      throw new Error('The comparison is taking longer than expected. Please try again.');
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'The comparison could not be completed.';
      setDiscrepancy((d) => ({ ...d, status: 'error', message: null, error: message }));
    }
  };

  const rejectToast = (rejected, overflow) => {
    if (rejected) toast.error(`${rejected} file(s) skipped — only PDF and Word documents are accepted`);
    if (overflow) toast.error(`Up to ${MAX_SUPPORTING_DOCS} supporting documents — ${overflow} skipped`);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex-shrink-0">
              <Edit3 className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Contract Redlining</h1>
          </div>
          <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-950">
            <p className="font-medium">Past redlines</p>
            <p className="mt-1 text-blue-900/90 leading-relaxed">
              After you run <strong>Analyze &amp; Redline</strong> while signed in, this session is saved. Open{' '}
              <Link
                to="/user-chat"
                className="font-semibold text-blue-800 underline underline-offset-2 hover:text-blue-950"
              >
                Chat History
              </Link>{' '}
              in the sidebar and look for <strong>Contract Redline</strong> rows (title starts with &quot;Redline · …&quot;).
            </p>
          </div>
        </div>

        {/* Upload Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Upload Contract</h2>
            {file && (
              <button
                onClick={clearFile}
                className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Clear
              </button>
            )}
          </div>

          {!file ? (
            <div className="flex flex-col md:flex-row gap-6">
              {/* Sample Documents Section */}
              <div className="w-full md:w-64 flex-shrink-0">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Sample Documents</h3>
                <div className="space-y-3">
                  <button
                    onClick={() => handleSampleDocument('/sample-contract.pdf', 'sample-contract.pdf')}
                    className="w-full flex items-center gap-3 p-3 border-2 border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-all text-left group"
                  >
                    <div className="p-2 bg-red-100 rounded group-hover:bg-red-200 transition-colors">
                      <FileText className="w-5 h-5 text-red-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">Sample Contract (PDF)</p>
                      <p className="text-xs text-gray-500">Construction contract template</p>
                    </div>
                  </button>
                  <button
                    onClick={() => handleSampleDocument('/sample-contract.doc', 'sample-contract.doc')}
                    className="w-full flex items-center gap-3 p-3 border-2 border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-all text-left group"
                  >
                    <div className="p-2 bg-blue-100 rounded group-hover:bg-blue-200 transition-colors">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">Sample Contract (Word)</p>
                      <p className="text-xs text-gray-500">Agreement template</p>
                    </div>
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-3 italic">Try these samples if you don't have a contract to upload</p>
              </div>

              {/* Upload Area */}
              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                className="flex-1 border-2 border-dashed border-gray-300 rounded-lg p-6 sm:p-12 text-center hover:border-blue-500 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-8 h-8 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-base sm:text-lg font-medium text-gray-900 mb-2">
                  Drop your contract here
                </p>
                <p className="text-xs sm:text-sm text-gray-500 mb-4 px-2">
                  Supports PDF and Word documents (.docx, .doc) - or click to browse
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.doc,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <FileText className="w-10 h-10 text-blue-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{file.name}</p>
                  <p className="text-sm text-gray-500">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <button
                  onClick={analyzeContract}
                  disabled={analyzing}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-lg hover:from-orange-700 hover:to-orange-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
                >
                  {analyzing ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span className="truncate max-w-[22rem]">{progressLabel || 'Analyzing...'}</span>
                    </>
                  ) : (
                    <>
                      <Zap className="w-5 h-5" />
                      Analyze & Redline
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Supporting documents for this contract — exhibits, addenda, supplements */}
          {file && !analysis && (
            <ReferenceDocumentsPicker
              files={referenceFiles}
              onChange={setReferenceFiles}
              disabled={analyzing}
              onReject={rejectToast}
            />
          )}
        </div>

        {analysis?.degraded && (
          <div className="mb-4 rounded-lg border-l-4 border-red-500 bg-red-50 px-4 py-3">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-red-900">This contract was not reviewed</p>
                <p className="text-sm text-red-800 mt-1">{analysis.degradedReason}</p>
                <p className="text-sm text-red-800 mt-1">
                  An empty or generic findings list here does <strong>not</strong> mean the contract
                  is clean — it means the pages could not be read.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Interactive PDF Viewer with Annotations */}
        {(analysis || analyzing || generatingPreview) && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Contract Review</h3>
                {analysis && (
                  <>
                    <div className="flex items-center gap-2">
                      <span className="text-xs sm:text-sm text-gray-600">Risk Score:</span>
                      <div className={`inline-flex items-center gap-2 px-2 sm:px-3 py-1 rounded-full ${
                        analysis.overallRisk === 'high' ? 'bg-red-100 text-red-700' :
                        analysis.overallRisk === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        <span className="text-base sm:text-lg font-bold">{analysis.riskScore || 'N/A'}</span>
                        <span className="text-xs font-medium">{analysis.overallRisk.toUpperCase()}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 px-2 sm:px-3 py-1 bg-blue-100 text-blue-700 rounded-full">
                      <Highlighter className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="text-xs sm:text-sm font-medium">{annotations.length} Annotations</span>
                    </div>
                    {/* The review could not read the document. This has to
                        shout, because an empty findings list otherwise reads
                        as a clean contract. */}
                    {analysis.degraded && (
                      <div className="flex items-center gap-2 px-2 sm:px-3 py-1 bg-red-100 text-red-800 rounded-full"
                           title={analysis.degradedReason || ''}>
                        <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="text-xs sm:text-sm font-semibold">Not reviewed</span>
                      </div>
                    )}
                    {/* How much of the contract this review actually read. A
                        truncated review that looks complete is the one failure
                        worth shouting about: "no finding" and "never read" are
                        indistinguishable to the reader otherwise. */}
                    {analysis.coverage && (
                      analysis.coverage.truncated ? (
                        <div className="flex items-center gap-2 px-2 sm:px-3 py-1 bg-amber-100 text-amber-800 rounded-full"
                             title={`Only the first ${Math.round(analysis.coverage.charsRead / 1000)}K of `
                               + `${Math.round(analysis.coverage.charsTotal / 1000)}K characters were reviewed. `
                               + `Clauses after that point were NOT read.`}>
                          <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span className="text-xs sm:text-sm font-medium">
                            Partial: first ~{analysis.coverage.pagesRead} of {analysis.coverage.pages} pages
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 px-2 sm:px-3 py-1 bg-green-100 text-green-700 rounded-full"
                             title={analysis.coverage.mode === 'sweep'
                               ? `Read in ${analysis.coverage.sections} sections`
                                 + `${analysis.coverage.reconciled ? ', then reconciled across the whole contract' : ''}`
                                 + `${analysis.coverage.sectionsFailed?.length ? ` — sections not read: ${analysis.coverage.sectionsFailed.join(', ')}` : ''}`
                               : 'The whole contract was reviewed in one pass'}>
                          <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span className="text-xs sm:text-sm font-medium">
                            All {analysis.coverage.pages} pages read
                            {analysis.coverage.conflicts > 0 && ` · ${analysis.coverage.conflicts} conflict(s)`}
                          </span>
                        </div>
                      )
                    )}
                  </>
                )}
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
                <button
                  onClick={() => setShowAnnotations(!showAnnotations)}
                  disabled={analyzing || generatingPreview}
                  className={`w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-sm ${
                    analyzing || generatingPreview
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : showAnnotations
                      ? 'bg-gradient-to-r from-orange-600 to-orange-700 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Highlighter className="w-4 h-4" />
                  {showAnnotations ? 'Hide' : 'Show'} Redlines
                </button>
                <button
                  onClick={downloadRedlinedPDF}
                  disabled={analyzing || generatingPreview}
                  className={`w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors shadow-md ${
                    analyzing || generatingPreview
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-orange-600 text-white hover:bg-orange-700 hover:shadow-lg'
                  }`}
                >
                  <Download className="w-5 h-5" />
                  Download {isWordDocument ? 'Word' : 'PDF'}
                </button>
              </div>
            </div>
            
            {(analyzing || generatingPreview) ? (
              <div className="border border-gray-200 rounded-lg p-12 text-center bg-gray-50">
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
                <p className="text-lg font-medium text-gray-900 mb-2">
                  {analyzing 
                    ? 'Analyzing contract with AI...' 
                    : isWordDocument 
                      ? 'Generating Annotated Document...' 
                      : 'Generating Annotated PDF...'}
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  {analyzing 
                    ? 'Extracting text and identifying risks...' 
                    : 'Adding redlines and highlights to your contract'}
                </p>
                {progress.estimated > 0 && (
                  <div className="max-w-md mx-auto space-y-2">
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                      <span>{analyzing ? 'Processing contract...' : 'Generating preview...'}</span>
                      <span className="font-medium">{Math.round(progress.percentage)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className="bg-gradient-to-r from-orange-600 to-orange-700 h-2.5 rounded-full transition-all duration-300"
                        style={{ width: `${progress.percentage}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>Elapsed: {Math.floor(progress.elapsed / 60)}:{(progress.elapsed % 60).toString().padStart(2, '0')}</span>
                      {progress.remaining > 0 && (
                        <span>Est. remaining: {Math.floor(progress.remaining / 60)}:{(progress.remaining % 60).toString().padStart(2, '0')}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : isWordDocument ? (
              <div className="border border-gray-200 rounded-lg p-12 text-center bg-gradient-to-br from-blue-50 to-indigo-50">
                <FileText className="w-24 h-24 text-blue-600 mx-auto mb-6" />
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {showAnnotations ? 'Redlined Word Document Ready' : 'Word Document Loaded'}
                </h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  {showAnnotations 
                    ? 'Your Word document has been analyzed and annotated with AI-powered redlines, comments, and highlights.'
                    : 'Word document uploaded successfully. Click "Analyze & Redline" to start AI analysis.'}
                </p>
                {showAnnotations && redlinedDocUrl && (
                  <div className="space-y-4">
                    <a
                      href={redlinedDocUrl}
                      download={`redlined_${file?.name || 'contract.docx'}`}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl font-medium"
                    >
                      <Download className="w-5 h-5" />
                      Download Redlined Word Document
                    </a>
                    <p className="text-sm text-gray-500">
                      Open in Microsoft Word or LibreOffice to view annotations
                    </p>
                  </div>
                )}
                <div className="mt-8 p-4 bg-white rounded-lg border border-blue-200 max-w-md mx-auto">
                  <p className="text-xs text-gray-600 leading-relaxed">
                    💡 <strong>Tip:</strong> Word documents contain tracked changes, comments, and highlighted risks that are best viewed in Microsoft Word, Google Docs, or LibreOffice Writer.
                  </p>
                </div>
              </div>
            ) : pdfUrl ? (
              <div className="border border-gray-200 rounded-lg bg-gray-100 overflow-x-auto overflow-y-auto" style={{ maxHeight: '800px' }}>
                <iframe
                  key={`pdf-${showAnnotations && annotatedPdfUrl ? annotatedPdfUrl : pdfUrl}`}
                  src={`${showAnnotations && annotatedPdfUrl ? annotatedPdfUrl : pdfUrl}#toolbar=1&navpanes=1&scrollbar=1&zoom=page-width`}
                  className="w-full"
                  style={{ minHeight: '800px', minWidth: '100%' }}
                  title="Contract Preview"
                  onLoad={() => {
                    console.log('✅ PDF loaded in iframe');
                    // Try to ensure sidebar is visible by adjusting iframe content
                    const iframe = document.querySelector('iframe[title="Contract Preview"]');
                    if (iframe && iframe.contentWindow) {
                      try {
                        // Some PDF viewers support this
                        iframe.contentWindow.scrollTo(0, 0);
                      } catch (e) {
                        // Cross-origin restrictions may prevent this
                        console.log('Could not access iframe content (expected for blob URLs)');
                      }
                    }
                  }}
                  onError={(e) => {
                    console.error('❌ iframe failed to load PDF:', e);
                    toast.error('PDF preview failed. The download button still works.');
                    setShowAnnotations(false);
                  }}
                />
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg p-12 text-center bg-gray-50">
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No document loaded</p>
              </div>
            )}

            {analysis && !analyzing && (
              <>
                <ReferenceDocumentsNote documents={metadata?.referenceDocuments} />
                <DiscrepancySection
                  contractName={file?.name || 'contract'}
                  state={discrepancy}
                  canRun={Boolean(file && analysis)}
                  onFilesChange={(next) => setDiscrepancy((d) => ({ ...d, files: next }))}
                  onRun={runDiscrepancyCheck}
                  onReject={rejectToast}
                />
              </>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default ContractRedline;
